import type { LocationPathSegment } from './schemas'

/**
 * Cible de navigation 3D dérivée d'un chemin de rangement (recherche → fly-to).
 * `locationId` = pièce ou meuble visible dans la scène ; `focusLocationId` = tiroir / case.
 */
export type SceneFocus = {
  placeId: string
  floor: number
  locationId: string
  focusLocationId?: string
  itemId?: string
}

type PathSeg = Pick<LocationPathSegment, 'id' | 'type'> & { floor?: number | null }

/** Résout lieu / étage / zone 3D (+ rangement interne) depuis un fil d'Ariane typé. */
export function sceneFocusFromPath(
  path: ReadonlyArray<PathSeg>,
  opts?: { locationId?: string | null; itemId?: string | null },
): SceneFocus | null {
  // Le lieu le plus profond du chemin : le bâtiment, quand le lieu du haut est un terrain (décision § 13).
  const place = [...path].reverse().find((p) => p.type === 'place')
  if (!place) return null
  const room = [...path].reverse().find((p) => p.type === 'room')
  const furniture = [...path].reverse().find((p) => p.type === 'furniture')
  const locationId = furniture?.id ?? room?.id
  if (!locationId) return null

  const leaf =
    opts?.locationId && path.some((p) => p.id === opts.locationId)
      ? opts.locationId
      : path[path.length - 1]?.id
  const focusLocationId =
    leaf && leaf !== locationId && leaf !== place.id ? leaf : undefined

  return {
    placeId: place.id,
    floor: room?.floor ?? 0,
    locationId,
    ...(focusLocationId ? { focusLocationId } : {}),
    ...(opts?.itemId ? { itemId: opts.itemId } : {}),
  }
}

/** Lien `/view3d?…` avec fly-to optionnel. */
export function view3dHref(focus: SceneFocus, options?: { fly?: boolean }): string {
  const q = new URLSearchParams({
    placeId: focus.placeId,
    floor: String(focus.floor),
    locationId: focus.locationId,
  })
  if (options?.fly !== false) q.set('fly', '1')
  if (focus.focusLocationId) {
    q.set('focus', focus.focusLocationId)
    q.set('interior', '1')
  }
  if (focus.itemId) q.set('itemId', focus.itemId)
  return `/view3d?${q}`
}
